import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft,
  Truck,
  Search,
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  CheckCircle,
  Save,
  Plus,
  Trash2,
  Calculator,
  Edit,
  RotateCcw,
  TrendingDown,
  Gavel,
  Upload,
  Link,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, getApiErrorMessage } from '../../lib/utils';

const API = API_BASE;

// ==================== SEMI-TRUCK DATA ====================
const SEMI_TRUCK_MAKES = [
  'Kenworth', 'Peterbilt', 'Freightliner', 'Volvo', 'International', 
  'Mack', 'Western Star', 'Hino', 'Isuzu', 'Navistar'
];

// Models by Semi-Truck Make
const SEMI_TRUCK_MODELS = {
  'Kenworth': ['T680', 'T880', 'W900', 'W990', 'T270', 'T370', 'T440', 'T470', 'T800'],
  'Peterbilt': ['579', '389', '567', '579 UltraLoft', '520', '536', '537', '548', '367'],
  'Freightliner': ['Cascadia', 'Coronado', 'Columbia', '122SD', 'M2 106', 'M2 112', 'Cascadia Evolution'],
  'Volvo': ['VNL 860', 'VNL 760', 'VNL 670', 'VNL 300', 'VNR 300', 'VNR 400', 'VNR 640'],
  'International': ['LT', 'LoneStar', 'Lonestar', 'LONESTAR', 'ProStar', 'HX', 'HV', 'MV', 'CV'],
  'Mack': ['Anthem', 'Pinnacle', 'Granite', 'LR', 'MD', 'MD6', 'MD7', 'MD Electric', 'TerraPro'],
  'Western Star': ['5700XE', '4900', '4700', '47X', '49X', '57X'],
  'Hino': ['XL7', 'XL8', 'L6', 'L7', 'M4', 'M5'],
  'Isuzu': ['FTR', 'FVR', 'NPR', 'NPR-HD', 'NQR', 'NRR'],
  'Navistar': ['LT', 'LoneStar', 'HX', 'HV', 'MV']
};

// Sleeper Types for Semi-Trucks (realistic options)
const SLEEPER_TYPES = [
  'Day Cab (No Sleeper)',
  'Flat Top Sleeper',
  'Mid-Roof Sleeper 48"',
  'Mid-Roof Sleeper 56"',
  'Mid-Roof Sleeper 60"',
  'Mid-Roof Sleeper 70"',
  'Mid-Roof Sleeper 72"',
  'Mid-Roof Sleeper 73"',
  'Raised Roof Sleeper 70"',
  'Raised Roof Sleeper 72"',
  'Raised Roof Sleeper 76"',
  'Raised Roof Sleeper 77"',
  'Raised Roof Sleeper 80"',
  'UltraLoft Sleeper 80"',
  'Studio Sleeper',
  'Condo Sleeper'
];

// Engine options for Semi-Trucks
const SEMI_TRUCK_ENGINES = [
  'Cummins X15',
  'Cummins X12',
  'Cummins ISX15',
  'Cummins ISX12',
  'Cummins ISL9',
  'Cummins B6.7',
  'Cummins L9',
  'PACCAR MX-13',
  'PACCAR MX-11',
  'Detroit DD15',
  'Detroit DD13',
  'Detroit DD16',
  'Volvo D13',
  'Volvo D11',
  'Volvo',
  'Mack MP8',
  'Mack MP7',
  'International A26',
  'Navistar N13',
  'Other'
];

// Normalize engine names from PDF extraction to match dropdown options
const normalizeEngine = (engine) => {
  if (!engine) return '';
  const engineLower = engine.toLowerCase();
  
  // Cummins engines
  if (engineLower.includes('cummins')) {
    if (engineLower.includes('x15')) return 'Cummins X15';
    if (engineLower.includes('x12')) return 'Cummins X12';
    if (engineLower.includes('isx15') || engineLower.includes('isx 15')) return 'Cummins ISX15';
    if (engineLower.includes('isx12') || engineLower.includes('isx 12')) return 'Cummins ISX12';
    if (engineLower.includes('isl9') || engineLower.includes('isl 9')) return 'Cummins ISL9';
    return 'Cummins X15'; // Default Cummins
  }
  
  // PACCAR engines
  if (engineLower.includes('paccar') || engineLower.includes('mx-') || engineLower.includes('mx13') || engineLower.includes('mx11')) {
    if (engineLower.includes('13') || engineLower.includes('mx-13')) return 'PACCAR MX-13';
    if (engineLower.includes('11') || engineLower.includes('mx-11')) return 'PACCAR MX-11';
    return 'PACCAR MX-13';
  }
  
  // Detroit engines
  if (engineLower.includes('detroit') || engineLower.includes('dd')) {
    if (engineLower.includes('16') || engineLower.includes('dd16')) return 'Detroit DD16';
    if (engineLower.includes('15') || engineLower.includes('dd15')) return 'Detroit DD15';
    if (engineLower.includes('13') || engineLower.includes('dd13')) return 'Detroit DD13';
    return 'Detroit DD15';
  }
  
  // Volvo engines
  if (engineLower.includes('volvo') || engineLower.includes('d13') || engineLower.includes('d11')) {
    if (engineLower.includes('d13') || engineLower.includes('13')) return 'Volvo D13';
    if (engineLower.includes('d11') || engineLower.includes('11')) return 'Volvo D11';
    return 'Volvo D13'; // Default Volvo
  }
  
  // Mack engines
  if (engineLower.includes('mack') || engineLower.includes('mp8') || engineLower.includes('mp7')) {
    if (engineLower.includes('mp8') || engineLower.includes('8')) return 'Mack MP8';
    if (engineLower.includes('mp7') || engineLower.includes('7')) return 'Mack MP7';
    return 'Mack MP8';
  }
  
  // International/Navistar engines
  if (engineLower.includes('international') || engineLower.includes('navistar') || engineLower.includes('a26') || engineLower.includes('n13')) {
    if (engineLower.includes('a26')) return 'International A26';
    if (engineLower.includes('n13')) return 'Navistar N13';
    return 'International A26';
  }
  
  // If in list, return as-is
  if (SEMI_TRUCK_ENGINES.includes(engine)) return engine;
  
  // Return original
  return engine;
};

// Axle configurations
const AXLE_CONFIGS = [
  '4x2 (Single Drive)',
  '6x2 (Single Drive + Tag)',
  '6x4 (Tandem Drive)',
  '8x4 (Tri-Drive)',
  '6x6 (All Wheel Drive)',
  '8x6 (All Wheel Drive)'
];

// Transmission options
const TRANSMISSIONS = [
  // Manual Transmissions
  'Eaton Fuller 10-Speed Manual',
  'Eaton Fuller 13-Speed Manual',
  'Eaton Fuller 18-Speed Manual',
  // Automated Manual Transmissions (AMT)
  'Eaton Ultrashift Plus 10-Speed',
  'Eaton Ultrashift Plus 13-Speed',
  'Eaton Endurant 12-Speed',
  'Eaton Fuller 12 Speed Endurant',
  'Eaton Fuller Endurant 12-Speed',
  'Eaton Endurant HD 18-Speed',
  'Eaton Endurant XD Pro 12-Speed',
  'Detroit DT12 Automated',
  'Detroit DT12-DA Automated',
  'Volvo I-Shift 12-Speed',
  'Volvo I-Shift 14-Speed',
  'PACCAR TX-12 Automated',
  'PACCAR TX-18 Automated',
  'Mack mDRIVE 12-Speed',
  'Mack mDRIVE 13-Speed',
  'Mack mDRIVE HD 14-Speed',
  // Automatic Transmissions
  'Allison 3000 Series Auto',
  'Allison 4000 Series Auto',
  'Allison 4700 RDS Auto',
  // Catch-all for custom/other transmissions
  'Automatic',
  'Manual',
  'Other'
];

// Normalize transmission names from PDF extraction to match dropdown options
const normalizeTransmission = (trans) => {
  if (!trans) return '';
  const transLower = trans.toLowerCase();
  
  // Eaton Endurant variations
  if (transLower.includes('endurant') && transLower.includes('12')) {
    if (transLower.includes('fuller')) return 'Eaton Fuller 12 Speed Endurant';
    return 'Eaton Endurant 12-Speed';
  }
  if (transLower.includes('endurant') && transLower.includes('18')) return 'Eaton Endurant HD 18-Speed';
  if (transLower.includes('endurant') && transLower.includes('xd')) return 'Eaton Endurant XD Pro 12-Speed';
  
  // Eaton Fuller manual
  if (transLower.includes('fuller') && transLower.includes('10')) return 'Eaton Fuller 10-Speed Manual';
  if (transLower.includes('fuller') && transLower.includes('13')) return 'Eaton Fuller 13-Speed Manual';
  if (transLower.includes('fuller') && transLower.includes('18')) return 'Eaton Fuller 18-Speed Manual';
  
  // Detroit
  if (transLower.includes('detroit') || transLower.includes('dt12')) {
    if (transLower.includes('da')) return 'Detroit DT12-DA Automated';
    return 'Detroit DT12 Automated';
  }
  
  // Volvo I-Shift
  if (transLower.includes('i-shift') || transLower.includes('ishift')) {
    if (transLower.includes('14')) return 'Volvo I-Shift 14-Speed';
    return 'Volvo I-Shift 12-Speed';
  }
  
  // Mack mDRIVE - handle various formats
  if (transLower.includes('mdrive') || transLower.includes('m-drive') || 
      (transLower.includes('mack') && (transLower.includes('drive') || transLower.includes('12') || transLower.includes('automated')))) {
    if (transLower.includes('14') || transLower.includes('hd')) return 'Mack mDRIVE HD 14-Speed';
    if (transLower.includes('13')) return 'Mack mDRIVE 13-Speed';
    return 'Mack mDRIVE 12-Speed';
  }
  
  // Allison
  if (transLower.includes('allison')) {
    if (transLower.includes('4700')) return 'Allison 4700 RDS Auto';
    if (transLower.includes('4000')) return 'Allison 4000 Series Auto';
    return 'Allison 3000 Series Auto';
  }
  
  // Generic automatic/manual
  if (transLower.includes('automatic') || transLower.includes('auto')) return 'Automatic';
  if (transLower.includes('manual')) return 'Manual';
  
  // If the exact value exists in options, use it
  if (TRANSMISSIONS.includes(trans)) return trans;
  
  // Return original if no match (will show in dropdown if added)
  return trans;
};

// Normalize sleeper type from PDF extraction to match dropdown options
const normalizeSleeper = (sleeper) => {
  if (!sleeper) return '';
  const sleeperLower = sleeper.toLowerCase();
  
  // Day Cab
  if (sleeperLower.includes('day') && sleeperLower.includes('cab')) return 'Day Cab (No Sleeper)';
  if (sleeperLower.includes('no sleeper')) return 'Day Cab (No Sleeper)';
  
  // Flat Top
  if (sleeperLower.includes('flat') && sleeperLower.includes('top')) return 'Flat Top Sleeper';
  
  // Mid-Roof Sleeper - extract size
  if (sleeperLower.includes('mid') && (sleeperLower.includes('roof') || sleeperLower.includes('rise'))) {
    if (sleeperLower.includes('48')) return 'Mid-Roof Sleeper 48"';
    if (sleeperLower.includes('56')) return 'Mid-Roof Sleeper 56"';
    if (sleeperLower.includes('60')) return 'Mid-Roof Sleeper 60"';
    if (sleeperLower.includes('70')) return 'Mid-Roof Sleeper 70"';
    if (sleeperLower.includes('72')) return 'Mid-Roof Sleeper 72"';
    if (sleeperLower.includes('73')) return 'Mid-Roof Sleeper 73"';
    return 'Mid-Roof Sleeper 56"'; // Default mid-roof
  }
  
  // Hi-Rise Sleeper (treat as Mid-Roof)
  if (sleeperLower.includes('hi') && sleeperLower.includes('rise')) {
    if (sleeperLower.includes('48')) return 'Mid-Roof Sleeper 48"';
    if (sleeperLower.includes('56')) return 'Mid-Roof Sleeper 56"';
    if (sleeperLower.includes('60')) return 'Mid-Roof Sleeper 60"';
    if (sleeperLower.includes('70')) return 'Mid-Roof Sleeper 70"';
    if (sleeperLower.includes('72')) return 'Mid-Roof Sleeper 72"';
    return 'Mid-Roof Sleeper 56"';
  }
  
  // Raised Roof Sleeper
  if (sleeperLower.includes('raised') && sleeperLower.includes('roof')) {
    if (sleeperLower.includes('70')) return 'Raised Roof Sleeper 70"';
    if (sleeperLower.includes('72')) return 'Raised Roof Sleeper 72"';
    if (sleeperLower.includes('76')) return 'Raised Roof Sleeper 76"';
    if (sleeperLower.includes('77')) return 'Raised Roof Sleeper 77"';
    if (sleeperLower.includes('80')) return 'Raised Roof Sleeper 80"';
    return 'Raised Roof Sleeper 72"'; // Default raised roof
  }
  
  // UltraLoft
  if (sleeperLower.includes('ultraloft')) return 'UltraLoft Sleeper 80"';
  
  // Studio/Condo
  if (sleeperLower.includes('studio')) return 'Studio Sleeper';
  if (sleeperLower.includes('condo')) return 'Condo Sleeper';
  
  // Generic sleeper - try to extract size
  if (sleeperLower.includes('sleeper')) {
    if (sleeperLower.includes('48')) return 'Mid-Roof Sleeper 48"';
    if (sleeperLower.includes('56')) return 'Mid-Roof Sleeper 56"';
    if (sleeperLower.includes('60')) return 'Mid-Roof Sleeper 60"';
    if (sleeperLower.includes('70')) return 'Mid-Roof Sleeper 70"';
    if (sleeperLower.includes('72')) return 'Mid-Roof Sleeper 72"';
    if (sleeperLower.includes('73')) return 'Mid-Roof Sleeper 73"';
    if (sleeperLower.includes('76')) return 'Raised Roof Sleeper 76"';
    if (sleeperLower.includes('77')) return 'Raised Roof Sleeper 77"';
    if (sleeperLower.includes('80')) return 'Raised Roof Sleeper 80"';
    return 'Mid-Roof Sleeper 56"'; // Default
  }
  
  // If exact match exists, use it
  if (SLEEPER_TYPES.includes(sleeper)) return sleeper;
  
  return sleeper;
};

// Normalize axle configuration from PDF extraction to match dropdown options
const normalizeAxleConfig = (axle) => {
  if (!axle) return '';
  const axleLower = axle.toLowerCase();
  
  // 4x2 Single
  if (axleLower.includes('4x2') || (axleLower.includes('single') && !axleLower.includes('tag'))) {
    return '4x2 (Single Drive)';
  }
  
  // 6x2 Single + Tag
  if (axleLower.includes('6x2') || (axleLower.includes('single') && axleLower.includes('tag'))) {
    return '6x2 (Single Drive + Tag)';
  }
  
  // 6x4 Tandem - most common
  if (axleLower.includes('6x4') || axleLower.includes('tandem')) {
    return '6x4 (Tandem Drive)';
  }
  
  // 8x4 Tri-Drive
  if (axleLower.includes('8x4') || axleLower.includes('tri')) {
    return '8x4 (Tri-Drive)';
  }
  
  // 6x6 All Wheel Drive
  if (axleLower.includes('6x6')) {
    return '6x6 (All Wheel Drive)';
  }
  
  // 8x6 All Wheel Drive
  if (axleLower.includes('8x6')) {
    return '8x6 (All Wheel Drive)';
  }
  
  // If exact match exists, use it
  if (AXLE_CONFIGS.includes(axle)) return axle;
  
  return axle;
};

// ==================== BOX TRUCK & REEFER DATA ====================
const BOX_TRUCK_MAKES = [
  'Freightliner', 'International', 'Isuzu', 'Hino', 'Ford', 
  'Chevrolet', 'GMC', 'Kenworth', 'Peterbilt', 'Mitsubishi Fuso',
  'Mack', 'Volvo'
];

const BOX_TRUCK_MODELS = {
  'Freightliner': ['M2 106', 'M2 112', 'P700', 'P1000', 'P1200'],
  'International': ['MV', 'CV', 'DuraStar', '4300', '4400'],
  'Isuzu': ['NPR', 'NPR-HD', 'NQR', 'NRR', 'FTR'],
  'Hino': ['155', '195', '268', '338', 'L6', 'L7'],
  'Ford': ['E-350', 'E-450', 'F-550', 'F-650', 'F-750'],
  'Chevrolet': ['Express 3500', 'LCF 4500', 'LCF 5500', 'Silverado 4500'],
  'GMC': ['Savana 3500', 'C4500', 'C5500', 'Sierra 4500'],
  'Kenworth': ['K270', 'K370', 'T270', 'T370'],
  'Peterbilt': ['220', '325', '330', '337', '348'],
  'Mitsubishi Fuso': ['FE160', 'FE180', 'FG4X4'],
  'Mack': ['MD6', 'MD7', 'MD Electric', 'LR', 'LR Electric', 'TerraPro'],
  'Volvo': ['VNL', 'VNR', 'VHD', 'FE', 'FMX']
};

const BOX_LENGTHS = [
  '12 ft', '14 ft', '16 ft', '18 ft', '20 ft', '22 ft', '24 ft', '26 ft', '28 ft'
];

// ==================== PICKUP TRUCK DATA ====================
const PICKUP_TRUCK_MAKES = [
  'Ford', 'Chevrolet', 'GMC', 'Ram', 'Toyota', 'Nissan', 
  'Honda', 'Dodge', 'Jeep', 'Rivian', 'Tesla'
];

const PICKUP_MODELS = {
  'Ford': ['F-150', 'F-250', 'F-350', 'F-450', 'Ranger', 'Maverick'],
  'Chevrolet': ['Silverado 1500', 'Silverado 2500', 'Silverado 3500', 'Colorado', 'S-10'],
  'GMC': ['Sierra 1500', 'Sierra 2500', 'Sierra 3500', 'Canyon'],
  'Ram': ['1500', '2500', '3500', 'ProMaster'],
  'Toyota': ['Tundra', 'Tacoma', 'Hilux'],
  'Nissan': ['Titan', 'Titan XD', 'Frontier'],
  'Honda': ['Ridgeline'],
  'Dodge': ['Ram 1500', 'Ram 2500', 'Ram 3500'],
  'Jeep': ['Gladiator'],
  'Rivian': ['R1T'],
  'Tesla': ['Cybertruck']
};

const CAB_TYPES = ['Regular Cab', 'Extended Cab', 'Super Cab', 'Crew Cab', 'Double Cab', 'Quad Cab', 'Mega Cab'];
const BED_LENGTHS = ['5.5 ft (Short)', '6.5 ft (Standard)', '8 ft (Long)'];
const DRIVE_TYPES = ['2WD (Rear)', '4WD (4x4)', 'AWD (All Wheel)'];

const PICKUP_ENGINES = [
  '2.3L EcoBoost I4',
  '2.7L EcoBoost V6',
  '3.0L Duramax Diesel I6',
  '3.5L EcoBoost V6',
  '3.5L PowerBoost Hybrid V6',
  '5.0L Coyote V8',
  '5.3L Vortec V8',
  '5.7L HEMI V8',
  '6.2L V8',
  '6.6L Duramax Diesel V8',
  '6.7L Cummins Diesel I6',
  '6.7L Power Stroke Diesel V8',
  '7.3L Godzilla V8'
];

// ==================== TRAILER DATA ====================
const TRAILER_MAKES = [
  'Wabash', 'Great Dane', 'Utility', 'Hyundai Translead', 'Stoughton',
  'Vanguard', 'MAC Trailer', 'Wilson Trailer', 'Travis Body & Trailer',
  'Fontaine', 'Kentucky', 'Manac', 'Polar Tank', 'Heil', 'Brenner',
  'Transcraft', 'Dorsey', 'East Manufacturing', 'Reitnouer', 'XL Specialized'
];

const TRAILER_TYPES = [
  { value: 'dry_van', label: 'Dry Van (53\' Standard)' },
  { value: 'dry_van_48', label: 'Dry Van (48\')' },
  { value: 'reefer', label: 'Refrigerated (Reefer)' },
  { value: 'flatbed', label: 'Flatbed' },
  { value: 'flatbed_step', label: 'Step Deck / Drop Deck' },
  { value: 'lowboy', label: 'Lowboy' },
  { value: 'tanker_dry', label: 'Tanker (Dry Bulk)' },
  { value: 'tanker_liquid', label: 'Tanker (Liquid)' },
  { value: 'tanker_chemical', label: 'Tanker (Chemical)' },
  { value: 'livestock', label: 'Livestock' },
  { value: 'grain_hopper', label: 'Grain Hopper' },
  { value: 'dump', label: 'Dump Trailer' },
  { value: 'curtain_side', label: 'Curtain Side / Conestoga' },
  { value: 'container_chassis', label: 'Container Chassis' },
  { value: 'car_carrier', label: 'Car Carrier / Auto Transport' },
  { value: 'logging', label: 'Logging Trailer' },
  { value: 'dolly', label: 'Converter Dolly' },
  { value: 'double', label: 'Double / Pup Trailer' }
];

const TRAILER_MODELS = {
  'Wabash': ['DuraPlate', 'ArcticLite', 'Duraplate HD', 'Composite', 'DryVan'],
  'Great Dane': ['Champion', 'Everest', 'Freedom', 'Alpine', 'CL Series'],
  'Utility': ['4000D-X', '3000R', 'VS2DC', 'VS2RA', '4000AE'],
  'Hyundai Translead': ['HT Composite', 'Nitro', 'PC110', 'HT Dry Van'],
  'Stoughton': ['Z-Plate', 'PureBlue', 'Velocity', 'AVW'],
  'Vanguard': ['VXP', 'Cimarron', 'Maxcube', 'Cool Globe'],
  'MAC Trailer': ['Pinnacle', 'Road Warrior', 'MVP', 'Steel MVP'],
  'Wilson Trailer': ['Pace Setter', 'Road Brute', 'Silverstar', 'DWH'],
  'Travis Body & Trailer': ['Steel Dump', 'Aluminum Dump', 'Bottom Dump'],

// ==================== DUMP TRUCK DATA ====================
};

const DUMP_TRUCK_MAKES = [
  'Kenworth', 'Peterbilt', 'Freightliner', 'Mack', 'International',
  'Western Star', 'Volvo', 'Sterling', 'Ford', 'Chevrolet', 'GMC'
];

const DUMP_TRUCK_MODELS = {
  'Kenworth': ['T880', 'T800', 'W900', 'T440', 'T470', 'C500'],
  'Peterbilt': ['567', '389', '379', '365', '348', '337', '330'],
  'Freightliner': ['122SD', '114SD', 'M2 106', 'M2 112', 'Cascadia'],
  'Mack': ['Granite', 'Pinnacle', 'Anthem', 'TerraPro'],
  'International': ['HX Series', 'HV Series', 'WorkStar', 'ProStar', 'PayStar', 'DuraStar'],
  'Western Star': ['4700SF', '4900', '4800', '4700'],
  'Volvo': ['VHD', 'VNL', 'VNR'],
  'Sterling': ['LT9500', 'L8500', 'A9500'],
  'Ford': ['F-750', 'F-650', 'L9000'],
  'Chevrolet': ['Kodiak', 'C7500', 'C8500'],
  'GMC': ['TopKick', 'C7500', 'C8500']
};

const DUMP_TRUCK_ENGINES = [
  'PACCAR MX-13 (455 HP)',
  'PACCAR MX-13 (425 HP)',
  'PACCAR MX-11 (430 HP)',
  'Cummins X15 (500 HP)',
  'Cummins X15 (450 HP)',
  'Cummins X12 (400 HP)',
  'Cummins ISX15 (450 HP)',
  'Cummins ISL9 (350 HP)',
  'Detroit DD15 (455 HP)',
  'Detroit DD13 (410 HP)',
  'Mack MP8 (445 HP)',
  'Mack MP7 (395 HP)',
  'International A26 (475 HP)',
  'Caterpillar C13',
  'Caterpillar C15'
];

const DUMP_BODY_TYPES = [
  { value: 'end_dump', label: 'End Dump (Volteo Trasero)' },
  { value: 'side_dump', label: 'Side Dump (Volteo Lateral)' },
  { value: 'bottom_dump', label: 'Bottom Dump (Volteo Inferior)' },
  { value: 'transfer_dump', label: 'Transfer Dump' },
  { value: 'super_dump', label: 'Super Dump' }
];

const DUMP_BODY_MAKES = [
  'Beau-Roc', 'Travis Body', 'J&J Truck Bodies', 'Crysteel', 'Henderson',
  'OX Bodies', 'Clement', 'Warren', 'Bibeau', 'Load King', 'Mac Dump Bodies'
];

// Additional trailer makes/models
const TRAILER_MODELS_ADDITIONAL = {
  'Fontaine': ['Infinity', 'Revolution', 'Xcalibur', 'Magnitude'],
  'Kentucky': ['Moving Floor', 'Double Drop', 'Super Beam'],
  'Manac': ['UltraPlate', 'SuperLite', 'Explorer'],
  'Polar Tank': ['8400', '9500', 'Deep Drop'],
  'Heil': ['Steel Tanker', 'Aluminum Tanker', 'Super Jumbo'],
  'Brenner': ['Food Grade', 'Chemical', 'Petroleum'],
  'Transcraft': ['Eagle', 'Eagle II', 'Eagle II Combo Flatbed', 'TL2000', 'W2'],
  'Dorsey': ['Flatbed', 'Drop Deck', 'Curtain Side'],
  'East Manufacturing': ['Genesis', 'Beast', 'MMX'],
  'Reitnouer': ['Big Bubba', 'MaxMiser', 'DropMiser'],
  'XL Specialized': ['Lowboy', 'Stretch', 'Mini Deck']
};

// ==================== COMMON DATA ====================
const CONDITIONS = ['Excelente', 'Muy Bueno', 'Bueno', 'Regular', 'Necesita Reparación'];

// Get makes by asset type
const getMakesByType = (assetType) => {
  switch (assetType) {
    case 'truck': return SEMI_TRUCK_MAKES;
    case 'dump_truck': return DUMP_TRUCK_MAKES;
    case 'box_truck': return BOX_TRUCK_MAKES;
    case 'reefer_truck': return BOX_TRUCK_MAKES;
    case 'pickup': return PICKUP_TRUCK_MAKES;
    case 'trailer': return TRAILER_MAKES;
    default: return SEMI_TRUCK_MAKES;
  }
};

// Get models by make and asset type
const getModelsByMake = (assetType, make) => {
  if (!make) return [];
  switch (assetType) {
    case 'truck': return SEMI_TRUCK_MODELS[make] || [];
    case 'dump_truck': return DUMP_TRUCK_MODELS[make] || [];
    case 'box_truck': return BOX_TRUCK_MODELS[make] || [];
    case 'reefer_truck': return BOX_TRUCK_MODELS[make] || [];
    case 'pickup': return PICKUP_MODELS[make] || [];
    case 'trailer': return TRAILER_MODELS[make] || [];
    default: return [];
  }
};

// Get engines by asset type
const getEnginesByType = (assetType) => {
  switch (assetType) {
    case 'truck': return SEMI_TRUCK_ENGINES;
    case 'dump_truck': return DUMP_TRUCK_ENGINES;
    case 'box_truck': return [...SEMI_TRUCK_ENGINES.slice(0, 8), 'Gasoline V8', 'Gasoline V10'];
    case 'reefer_truck': return [...SEMI_TRUCK_ENGINES.slice(0, 8), 'Gasoline V8', 'Gasoline V10'];
    case 'pickup': return PICKUP_ENGINES;
    default: return [];
  }
};

const AssetAppraisalApp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client');
  
  const [appraisal, setAppraisal] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [searchingComps, setSearchingComps] = useState(false);
  const [searchingAuction, setSearchingAuction] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [clients, setClients] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [customModel, setCustomModel] = useState('');
  
  // Import from PDF
  const [importing, setImporting] = useState(false);
  
  // Blind price - hidden until reveal
  const [priceRevealed, setPriceRevealed] = useState(false);
  
  const [formData, setFormData] = useState({
    asset_type: 'truck',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    mileage: '',
    sleeper_type: '',
    axle_config: '',
    engine_make: '',
    transmission: '',
    condition: '',
    body_length: '',
    requested_price: '',
    cab_type: '',
    bed_length: '',
    drive_type: '',
    trailer_type: '',
    // New technical fields
    vin: '',
    wheelbase: '',
    horsepower: '',
    engine_displacement: '',
    gvwr: '',
    fuel_type: '',
    suspension_type: '',
    // Commercial work truck fields
    engine_hours: '',
    tire_condition: '',
    color: '',
    wheels: '',
    equipment: '',
    body_type: '',
    tires: ''
  });
  const [selectedClient, setSelectedClient] = useState(clientId || undefined);
  const [notes, setNotes] = useState('');

  // Computed values
  const availableMakes = getMakesByType(formData.asset_type);
  const availableModels = getModelsByMake(formData.asset_type, formData.make);
  const availableEngines = getEnginesByType(formData.asset_type);

  // Reset form when navigating to 'new' or when id changes
  useEffect(() => {
    fetchClients();
    if (id === 'new' || !id) {
      // Always reset for new appraisal
      setAppraisal(null);
      setFormData({
        asset_type: 'truck',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        mileage: '',
        sleeper_type: '',
        axle_config: '',
        engine_make: '',
        transmission: '',
        condition: '',
        body_length: '',
        requested_price: '',
        cab_type: '',
        bed_length: '',
        drive_type: '',
        trailer_type: '',
        vin: '',
        wheelbase: '',
        horsepower: '',
        engine_displacement: '',
        gvwr: '',
        fuel_type: '',
        suspension_type: '',
        engine_hours: '',
        tire_condition: '',
        color: '',
        wheels: '',
        equipment: '',
        body_type: '',
        tires: ''
      });
      setSelectedClient(clientId || undefined);
      setNotes('');
      setCustomModel('');
      setEditMode(false);
      setLoading(false);
    } else if (id) {
      fetchAppraisal();
    }
  }, [id, clientId]);

  const resetForm = () => {
    setAppraisal(null);
    setFormData({
      asset_type: 'truck',
      make: '',
      model: '',
      year: new Date().getFullYear(),
      mileage: '',
      sleeper_type: '',
      axle_config: '',
      engine_make: '',
      transmission: '',
      condition: '',
      body_length: '',
      requested_price: '',
      cab_type: '',
      bed_length: '',
      drive_type: '',
      trailer_type: '',
      vin: '',
      wheelbase: '',
      horsepower: '',
      engine_displacement: '',
      gvwr: '',
      fuel_type: '',
      suspension_type: '',
      engine_hours: '',
      tire_condition: '',
      color: '',
      wheels: '',
      equipment: '',
      body_type: '',
      tires: ''
    });
    setSelectedClient(clientId || undefined);
    setNotes('');
    setCustomModel('');
    setEditMode(false);
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchAppraisal = async () => {
    try {
      const response = await axios.get(`${API}/appraisals/${id}`);
      setAppraisal(response.data);
      setFormData(response.data.asset);
      setSelectedClient(response.data.client_id || undefined);
      setNotes(response.data.notes || '');
    } catch (error) {
      toast.error('Error al cargar la tasación');
      navigate('/apps/appraisal');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    const finalModel = formData.model === 'other' ? customModel : formData.model;
    if (!formData.make || !finalModel || !formData.year) {
      toast.error('Marca, Modelo y Año son requeridos');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        asset: {
          ...formData,
          model: finalModel,
          year: parseInt(formData.year),
          mileage: formData.mileage ? parseInt(formData.mileage) : null,
          requested_price: formData.requested_price ? parseFloat(formData.requested_price) : null
        },
        client_id: selectedClient && selectedClient !== 'none' ? selectedClient : null,
        notes
      };
      
      const response = await axios.post(`${API}/appraisals`, payload);
      toast.success('Tasación creada');
      navigate(`/apps/appraisal/${response.data.id}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al crear tasación'));
    } finally {
      setSaving(false);
    }
  };

  // Import specs from TruckPaper URL
  const handleImportFromUrl = async () => {
    if (!importUrl) {
      toast.error('Ingrese una URL de TruckPaper');
      return;
    }
    
    setImporting(true);
    try {
      const response = await axios.post(`${API}/appraisals/import-spec`, {
        url: importUrl
      });
      
      if (response.data.success) {
        const data = response.data.extracted_data;
        
        // Update form with extracted data
        setFormData(prev => ({
          ...prev,
          asset_type: data.asset_type || prev.asset_type,
          make: data.make || prev.make,
          model: data.model || prev.model,
          year: data.year || prev.year,
          mileage: data.mileage || prev.mileage,
          requested_price: data.requested_price || prev.requested_price,
          engine_make: normalizeEngine(data.engine_make) || prev.engine_make,
          horsepower: data.horsepower || prev.horsepower,
          transmission: normalizeTransmission(data.transmission) || prev.transmission,
          sleeper_type: normalizeSleeper(data.sleeper_type) || prev.sleeper_type,
          axle_config: normalizeAxleConfig(data.axle_config) || prev.axle_config,
          wheelbase: data.wheelbase || prev.wheelbase,
          fuel_type: data.fuel_type || prev.fuel_type,
          gvwr: data.gvwr || prev.gvwr,
          vin: data.vin || prev.vin,
          condition: data.condition || prev.condition,
          // Trailer-specific fields
          trailer_type: data.trailer_type || prev.trailer_type,
          body_length: data.body_length || prev.body_length,
          suspension_type: data.suspension_type || prev.suspension_type,
          // Pickup/Chassis Cab fields
          cab_type: data.cab_type || prev.cab_type,
          bed_length: data.bed_length || prev.bed_length,
          drive_type: data.drive_type || prev.drive_type,
          body_type: data.body_type || prev.body_type,
          // Commercial work truck fields
          engine_hours: data.engine_hours || prev.engine_hours,
          tire_condition: data.tire_condition || prev.tire_condition,
          color: data.color || prev.color,
          wheels: data.wheels || prev.wheels,
          equipment: data.equipment || prev.equipment
        }));
        
        toast.success('Especificaciones importadas exitosamente');
        setShowImportDialog(false);
        setImportUrl('');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al importar especificaciones'));
    } finally {
      setImporting(false);
    }
  };

  const handleUpdate = async () => {
    if (!appraisal) return;
    
    const finalModel = formData.model === 'other' ? customModel : formData.model;
    
    setSaving(true);
    try {
      const payload = {
        asset: {
          ...formData,
          model: finalModel,
          year: parseInt(formData.year),
          mileage: formData.mileage ? parseInt(formData.mileage) : null,
          requested_price: formData.requested_price ? parseFloat(formData.requested_price) : null
        },
        client_id: selectedClient && selectedClient !== 'none' ? selectedClient : null,
        notes,
        reset_comps: true  // Flag to reset comparables when editing
      };
      
      await axios.put(`${API}/appraisals/${appraisal.id}`, payload);
      toast.success('Tasación actualizada - Busque nuevos comparables');
      setEditMode(false);
      fetchAppraisal();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al actualizar'));
    } finally {
      setSaving(false);
    }
  };

  const handleSearchComps = async () => {
    if (!appraisal) return;
    
    setSearchingComps(true);
    try {
      const response = await axios.post(`${API}/appraisals/${appraisal.id}/search-comps`);
      const { count, source, message } = response.data;
      
      if (source === 'real') {
        toast.success(`¡${count} comparables REALES encontrados!`);
      } else {
        toast.info(`${count} comparables estimados (${message})`);
      }
      fetchAppraisal();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al buscar comparables'));
    } finally {
      setSearchingComps(false);
    }
  };

  const handleSearchAuction = async () => {
    if (!appraisal) return;
    
    setSearchingAuction(true);
    try {
      const response = await axios.post(`${API}/appraisals/${appraisal.id}/search-auction`);
      const { count, source, average_auction_price, message } = response.data;
      
      if (source === 'live') {
        toast.success(`¡${count} precios de subasta EN VIVO encontrados! Promedio: $${average_auction_price?.toLocaleString()}`);
      } else {
        toast.info(`${count} valores de subasta estimados - $${average_auction_price?.toLocaleString()} (${message})`);
      }
      fetchAppraisal();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al buscar datos de subasta'));
    } finally {
      setSearchingAuction(false);
    }
  };

  const handleCalculate = async () => {
    if (!appraisal) return;
    
    setCalculating(true);
    try {
      const response = await axios.put(`${API}/appraisals/${appraisal.id}/calculate`);
      toast.success('Valor tasado calculado');
      fetchAppraisal();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al calcular'));
    } finally {
      setCalculating(false);
    }
  };

  const handleMakeChange = (value) => {
    setFormData({
      ...formData,
      make: value,
      model: '' // Reset model when make changes
    });
  };

  const handleAssetTypeChange = (value) => {
    setFormData({
      ...formData,
      asset_type: value,
      make: '',
      model: '',
      sleeper_type: '',
      trailer_type: '',
      cab_type: '',
      bed_length: '',
      drive_type: '',
      axle_config: '',
      engine_make: '',
      transmission: ''
    });
  };

  const handleResetAndEdit = () => {
    setEditMode(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Form fields component - reusable for both new and edit modes
  const renderFormFields = () => (
    <div className="space-y-6">
      {/* Upload PDF Spec - MAIN FEATURE */}
      <div className="flex items-center gap-4 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border-2 border-green-400 dark:border-green-600">
        <Upload className="w-8 h-8 text-green-600" />
        <div className="flex-1">
          <p className="font-semibold text-green-800 dark:text-green-200">📄 Subir PDF del Spec</p>
          <p className="text-sm text-green-600 dark:text-green-300">Cargue el PDF del spec y el sistema extraerá automáticamente todos los datos</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept=".pdf"
            id="pdf-upload"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              
              if (!file.name.toLowerCase().endsWith('.pdf')) {
                toast.error('Solo se aceptan archivos PDF');
                return;
              }
              
              setImporting(true);
              try {
                const uploadFormData = new FormData();
                uploadFormData.append('file', file);
                
                const response = await axios.post(`${API}/appraisals/import-spec-pdf`, uploadFormData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                if (response.data.success) {
                  const data = response.data.extracted_data;
                  
                  // Helper to normalize make names for matching with our lists
                  const normalizeMake = (make, assetType) => {
                    if (!make) return '';
                    const makeUpper = make.toUpperCase();
                    const makes = getMakesByType(assetType);
                    // Try to find a match (case-insensitive)
                    const match = makes.find(m => m.toUpperCase() === makeUpper);
                    return match || make; // Return matched or original
                  };
                  
                  // Helper to normalize model names (case-insensitive match)
                  const normalizeModel = (model, make, assetType) => {
                    if (!model) return '';
                    const modelUpper = model.toUpperCase();
                    const models = getModelsByMake(assetType, make);
                    // Try to find a match (case-insensitive)
                    const match = models.find(m => m.toUpperCase() === modelUpper);
                    return match || model; // Return matched or original
                  };
                  
                  // Determine the asset type first
                  const newAssetType = data.asset_type || formData.asset_type;
                  const normalizedMake = normalizeMake(data.make, newAssetType);
                  const normalizedModel = normalizeModel(data.model, normalizedMake, newAssetType);
                  
                  // Build the update object based on asset type
                  const updateData = {
                    ...formData,
                    asset_type: newAssetType,
                    make: normalizedMake,
                    model: normalizedModel,
                    year: data.year || formData.year,
                    requested_price: data.requested_price || formData.requested_price,
                    vin: data.vin || formData.vin,
                    condition: data.condition || formData.condition,
                    axle_config: normalizeAxleConfig(data.axle_config) || formData.axle_config,
                    gvwr: data.gvwr || formData.gvwr
                  };
                  
                  // Add type-specific fields
                  if (newAssetType === 'trailer') {
                    // Trailer-specific fields
                    updateData.trailer_type = data.trailer_type || formData.trailer_type;
                    updateData.body_length = data.body_length || formData.body_length;
                    updateData.suspension_type = data.suspension_type || formData.suspension_type;
                    // Clear truck-specific fields for trailers
                    updateData.mileage = '';
                    updateData.engine_make = '';
                    updateData.sleeper_type = '';
                    updateData.horsepower = '';
                    updateData.fuel_type = '';
                  } else {
                    // Truck-specific fields (applies to truck, box_truck, pickup, chassis_cab)
                    updateData.mileage = data.mileage || formData.mileage;
                    // Normalize engine to match dropdown options
                    updateData.engine_make = normalizeEngine(data.engine_make) || formData.engine_make;
                    updateData.horsepower = data.horsepower || formData.horsepower;
                    // Normalize transmission to match dropdown options
                    updateData.transmission = normalizeTransmission(data.transmission) || formData.transmission;
                    // Normalize sleeper type to match dropdown options
                    updateData.sleeper_type = normalizeSleeper(data.sleeper_type) || formData.sleeper_type;
                    updateData.wheelbase = data.wheelbase || formData.wheelbase;
                    updateData.fuel_type = data.fuel_type || formData.fuel_type;
                    
                    // Pickup/Chassis Cab specific fields
                    updateData.cab_type = data.cab_type || formData.cab_type;
                    updateData.bed_length = data.bed_length || formData.bed_length;
                    updateData.drive_type = data.drive_type || formData.drive_type;
                    updateData.body_type = data.body_type || formData.body_type;
                    
                    // Commercial work truck fields
                    updateData.engine_hours = data.engine_hours || formData.engine_hours;
                    updateData.tire_condition = data.tire_condition || formData.tire_condition;
                    updateData.color = data.color || formData.color;
                    updateData.wheels = data.wheels || formData.wheels;
                    updateData.equipment = data.equipment || formData.equipment;
                  }
                  
                  setFormData(updateData);
                  
                  // Show detailed success message
                  const typeLabel = newAssetType === 'trailer' ? 'Trailer' : 
                                   newAssetType === 'truck' ? 'Semi-Truck' :
                                   newAssetType === 'box_truck' ? 'Box Truck' : 'Pickup';
                  toast.success(`✅ ${typeLabel} detectado: ${data.year || ''} ${normalizedMake} ${data.model || ''}`);
                }
              } catch (error) {
                toast.error(getApiErrorMessage(error, 'Error al procesar PDF'));
              } finally {
                setImporting(false);
                e.target.value = ''; // Reset file input
              }
            }}
          />
          <Button 
            onClick={() => document.getElementById('pdf-upload')?.click()}
            disabled={importing}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {importing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Subir PDF
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Client Selection */}
      <div className="space-y-2">
        <Label>Cliente (Opcional)</Label>
        <Select value={selectedClient} onValueChange={setSelectedClient}>
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar cliente..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sin cliente</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Asset Type and Basic Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Tipo de Activo *</Label>
          <Select value={formData.asset_type} onValueChange={handleAssetTypeChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="truck">Semi-Truck (Tractocamión)</SelectItem>
              <SelectItem value="dump_truck">Dump Truck (Camión de Volteo)</SelectItem>
              <SelectItem value="box_truck">Box Truck (Camión de Caja)</SelectItem>
              <SelectItem value="chassis_cab">Chassis Cab / Work Truck</SelectItem>
              <SelectItem value="reefer_truck">Reefer Truck (Camión Refrigerado)</SelectItem>
              <SelectItem value="pickup">Pickup Truck</SelectItem>
              <SelectItem value="trailer">Trailer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Marca *</Label>
          <Select value={formData.make} onValueChange={handleMakeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {availableMakes.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Modelo *</Label>
          <Select 
            value={formData.model === 'other' || !availableModels.includes(formData.model) && formData.model ? 'other' : formData.model} 
            onValueChange={(v) => {
              if (v === 'other') {
                setFormData({...formData, model: 'other'});
                setCustomModel('');
              } else {
                setFormData({...formData, model: v});
                setCustomModel('');
              }
            }}
            disabled={!formData.make || availableModels.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder={formData.make ? "Seleccionar..." : "Primero seleccione marca"} />
            </SelectTrigger>
            <SelectContent>
              {availableModels.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
              <SelectItem value="other">Otro (Especificar)</SelectItem>
            </SelectContent>
          </Select>
          {(formData.model === 'other' || customModel) && (
            <Input
              placeholder="Especifique el modelo..."
              className="mt-2"
              value={customModel}
              onChange={(e) => setCustomModel(e.target.value)}
            />
          )}
        </div>
        
        <div className="space-y-2">
          <Label>Año *</Label>
          <Select 
            value={String(formData.year)} 
            onValueChange={(v) => setFormData({...formData, year: parseInt(v)})}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar año..." />
            </SelectTrigger>
            <SelectContent>
              {Array.from({length: 27}, (_, i) => 2026 - i).map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mileage, Condition, Price */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label>Millaje</Label>
          <Input
            type="number"
            value={formData.mileage}
            onChange={(e) => setFormData({...formData, mileage: e.target.value})}
            placeholder={['truck', 'box_truck', 'reefer_truck'].includes(formData.asset_type) ? "Ej: 400,000" : "Ej: 75,000"}
          />
        </div>
        
        <div className="space-y-2">
          <Label>Condición</Label>
          <Select value={formData.condition} onValueChange={(v) => setFormData({...formData, condition: v})}>
            <SelectTrigger>
              <SelectValue placeholder="Seleccionar..." />
            </SelectTrigger>
            <SelectContent>
              {CONDITIONS.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2 col-span-2">
          <div className="flex items-center justify-between">
            <Label>Precio Solicitado (Blind)</Label>
            <Button 
              type="button"
              variant="ghost" 
              size="sm"
              onClick={() => setPriceRevealed(!priceRevealed)}
              className="text-xs"
            >
              {priceRevealed ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              {priceRevealed ? 'Ocultar' : 'Revelar'}
            </Button>
          </div>
          <div className="relative">
            <Input
              type={priceRevealed ? "number" : "password"}
              value={formData.requested_price}
              onChange={(e) => setFormData({...formData, requested_price: e.target.value})}
              placeholder="Ingrese precio..."
              className={!priceRevealed && formData.requested_price ? "font-mono tracking-widest" : ""}
            />
            {!priceRevealed && formData.requested_price && (
              <p className="text-xs text-muted-foreground mt-1">
                El precio NO afecta el cálculo de tasación. Se revelará al final.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Semi-Truck specific fields */}
      {formData.asset_type === 'truck' && (
        <>
          <Separator />
          <h3 className="font-semibold text-sm text-muted-foreground">Especificaciones de Semi-Truck</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Tipo de Sleeper</Label>
              <Select value={formData.sleeper_type} onValueChange={(v) => setFormData({...formData, sleeper_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {SLEEPER_TYPES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Motor</Label>
              <Select value={formData.engine_make} onValueChange={(v) => setFormData({...formData, engine_make: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {/* Include current value if not in list */}
                  {formData.engine_make && !SEMI_TRUCK_ENGINES.includes(formData.engine_make) && (
                    <SelectItem key={formData.engine_make} value={formData.engine_make}>
                      {formData.engine_make}
                    </SelectItem>
                  )}
                  {SEMI_TRUCK_ENGINES.map((e) => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Configuración de Ejes</Label>
              <Select value={formData.axle_config} onValueChange={(v) => setFormData({...formData, axle_config: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {AXLE_CONFIGS.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Transmisión</Label>
              <Select value={formData.transmission} onValueChange={(v) => setFormData({...formData, transmission: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {/* Include current value if not in list */}
                  {formData.transmission && !TRANSMISSIONS.includes(formData.transmission) && (
                    <SelectItem key={formData.transmission} value={formData.transmission}>
                      {formData.transmission}
                    </SelectItem>
                  )}
                  {TRANSMISSIONS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Additional Technical Specs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="space-y-2">
              <Label>VIN (Opcional)</Label>
              <Input 
                placeholder="Ej: 1XKAD49X..."
                value={formData.vin}
                onChange={(e) => setFormData({...formData, vin: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Wheelbase (pulg.)</Label>
              <Input 
                placeholder="Ej: 236"
                value={formData.wheelbase}
                onChange={(e) => setFormData({...formData, wheelbase: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Potencia (HP)</Label>
              <Input 
                type="number"
                placeholder="Ej: 450"
                value={formData.horsepower}
                onChange={(e) => setFormData({...formData, horsepower: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Tipo de Combustible</Label>
              <Select value={formData.fuel_type} onValueChange={(v) => setFormData({...formData, fuel_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Diesel">Diesel</SelectItem>
                  <SelectItem value="Gasoline">Gasolina</SelectItem>
                  <SelectItem value="Natural Gas">Gas Natural</SelectItem>
                  <SelectItem value="Electric">Eléctrico</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="space-y-2">
              <Label>Cilindrada Motor</Label>
              <Input 
                placeholder="Ej: 15L"
                value={formData.engine_displacement}
                onChange={(e) => setFormData({...formData, engine_displacement: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>GVWR (Peso Bruto)</Label>
              <Select value={formData.gvwr} onValueChange={(v) => setFormData({...formData, gvwr: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Class 7: 26,001-33,000 lbs">Clase 7: 26,001-33,000 lbs</SelectItem>
                  <SelectItem value="Class 8: 33,001+ lbs">Clase 8: 33,001+ lbs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Tipo de Suspensión</Label>
              <Select value={formData.suspension_type} onValueChange={(v) => setFormData({...formData, suspension_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Air Ride">Air Ride</SelectItem>
                  <SelectItem value="Leaf Spring">Leaf Spring</SelectItem>
                  <SelectItem value="Air Ride Front/Rear">Air Ride Frontal/Trasera</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Condición</Label>
              <Select value={formData.condition} onValueChange={(v) => setFormData({...formData, condition: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Excellent">Excelente</SelectItem>
                  <SelectItem value="Good">Bueno</SelectItem>
                  <SelectItem value="Fair">Regular</SelectItem>
                  <SelectItem value="Poor">Malo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      {/* Box Truck / Reefer Truck specific fields */}
      {['box_truck', 'reefer_truck'].includes(formData.asset_type) && (
        <>
          <Separator />
          <h3 className="font-semibold text-sm text-muted-foreground">
            Especificaciones de {formData.asset_type === 'reefer_truck' ? 'Camión Refrigerado' : 'Box Truck'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Longitud de Caja</Label>
              <Select value={formData.body_length} onValueChange={(v) => setFormData({...formData, body_length: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {BOX_LENGTHS.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Motor</Label>
              <Select value={formData.engine_make} onValueChange={(v) => setFormData({...formData, engine_make: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {/* Include current value if not in list */}
                  {formData.engine_make && !availableEngines.includes(formData.engine_make) && (
                    <SelectItem key={formData.engine_make} value={formData.engine_make}>
                      {formData.engine_make}
                    </SelectItem>
                  )}
                  {availableEngines.map((e) => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Transmisión</Label>
              <Select value={formData.transmission} onValueChange={(v) => setFormData({...formData, transmission: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {/* Include current value if not in list */}
                  {formData.transmission && !TRANSMISSIONS.includes(formData.transmission) && (
                    <SelectItem key={formData.transmission} value={formData.transmission}>
                      {formData.transmission}
                    </SelectItem>
                  )}
                  {TRANSMISSIONS.slice(0, 8).map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                  <SelectItem value="Automatic">Automática</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Configuración de Ejes</Label>
              <Select value={formData.axle_config} onValueChange={(v) => setFormData({...formData, axle_config: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4x2">4x2 (Single)</SelectItem>
                  <SelectItem value="6x4">6x4 (Tandem)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      {/* Pickup truck specific fields */}
      {formData.asset_type === 'pickup' && (
        <>
          <Separator />
          <h3 className="font-semibold text-sm text-muted-foreground">Especificaciones de Pickup</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Tipo de Cabina</Label>
              <Select value={formData.cab_type} onValueChange={(v) => setFormData({...formData, cab_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {CAB_TYPES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Longitud de Caja</Label>
              <Select value={formData.bed_length} onValueChange={(v) => setFormData({...formData, bed_length: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {BED_LENGTHS.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Tracción</Label>
              <Select value={formData.drive_type} onValueChange={(v) => setFormData({...formData, drive_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {DRIVE_TYPES.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Motor</Label>
              <Select value={formData.engine_make} onValueChange={(v) => setFormData({...formData, engine_make: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {PICKUP_ENGINES.map((e) => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      {/* Chassis Cab / Work Truck specific fields */}
      {formData.asset_type === 'chassis_cab' && (
        <>
          <Separator />
          <h3 className="font-semibold text-sm text-muted-foreground">Especificaciones de Chassis Cab / Work Truck</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Motor</Label>
              <Select value={formData.engine_make} onValueChange={(v) => setFormData({...formData, engine_make: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cummins 6.7L">Cummins 6.7L</SelectItem>
                  <SelectItem value="Ford 6.7L Power Stroke">Ford 6.7L Power Stroke</SelectItem>
                  <SelectItem value="GM Duramax 6.6L">GM Duramax 6.6L</SelectItem>
                  <SelectItem value="Hino J08E">Hino J08E</SelectItem>
                  <SelectItem value="Isuzu 5.2L">Isuzu 5.2L</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Potencia (HP)</Label>
              <Input 
                placeholder="Ej: 325"
                value={formData.horsepower}
                onChange={(e) => setFormData({...formData, horsepower: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Tipo de Cabina</Label>
              <Select value={formData.cab_type} onValueChange={(v) => setFormData({...formData, cab_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Regular Cab">Regular Cab</SelectItem>
                  <SelectItem value="Crew Cab">Crew Cab</SelectItem>
                  <SelectItem value="Extended Cab">Extended Cab</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Tipo de Cuerpo</Label>
              <Select value={formData.body_type} onValueChange={(v) => setFormData({...formData, body_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Flatbed">Flatbed</SelectItem>
                  <SelectItem value="Service Body">Service Body</SelectItem>
                  <SelectItem value="Dump Body">Dump Body</SelectItem>
                  <SelectItem value="Utility Bed">Utility Bed</SelectItem>
                  <SelectItem value="Stake Bed">Stake Bed</SelectItem>
                  <SelectItem value="Landscape">Landscape</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="space-y-2">
              <Label>Horas de Motor</Label>
              <Input 
                placeholder="Ej: 9631"
                value={formData.engine_hours}
                onChange={(e) => setFormData({...formData, engine_hours: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Condición de Llantas</Label>
              <Select value={formData.tire_condition} onValueChange={(v) => setFormData({...formData, tire_condition: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New">Nuevas (100%)</SelectItem>
                  <SelectItem value="80%">80%</SelectItem>
                  <SelectItem value="60%">60%</SelectItem>
                  <SelectItem value="50%">50%</SelectItem>
                  <SelectItem value="40%">40%</SelectItem>
                  <SelectItem value="Needs Replacement">Necesitan Reemplazo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Color</Label>
              <Input 
                placeholder="Ej: Blanco"
                value={formData.color}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Tipo de Rines</Label>
              <Select value={formData.wheels} onValueChange={(v) => setFormData({...formData, wheels: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Steel">Acero</SelectItem>
                  <SelectItem value="Aluminum">Aluminio</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <Label>GVWR</Label>
              <Input 
                placeholder="Ej: 16,500 lbs"
                value={formData.gvwr}
                onChange={(e) => setFormData({...formData, gvwr: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Equipo Adicional</Label>
              <Input 
                placeholder="Ej: Compresor de aire, grúa, lift gate"
                value={formData.equipment}
                onChange={(e) => setFormData({...formData, equipment: e.target.value})}
              />
            </div>
          </div>
        </>
      )}

      {/* Trailer specific fields */}
      {formData.asset_type === 'trailer' && (
        <>
          <Separator />
          <h3 className="font-semibold text-sm text-muted-foreground">Especificaciones de Trailer</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2 col-span-2 md:col-span-1">
              <Label>Tipo de Trailer *</Label>
              <Select value={formData.trailer_type} onValueChange={(v) => setFormData({...formData, trailer_type: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {TRAILER_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Longitud</Label>
              <Select value={formData.body_length} onValueChange={(v) => setFormData({...formData, body_length: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="28 ft">28 ft (Pup)</SelectItem>
                  <SelectItem value="40 ft">40 ft</SelectItem>
                  <SelectItem value="45 ft">45 ft</SelectItem>
                  <SelectItem value="48 ft">48 ft</SelectItem>
                  <SelectItem value="53 ft">53 ft (Standard)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Configuración de Ejes</Label>
              <Select value={formData.axle_config} onValueChange={(v) => setFormData({...formData, axle_config: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single Axle</SelectItem>
                  <SelectItem value="tandem">Tandem (2 Axles)</SelectItem>
                  <SelectItem value="spread">Spread Tandem</SelectItem>
                  <SelectItem value="tridem">Tridem (3 Axles)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      <div className="space-y-2">
        <Label>Notas</Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notas adicionales sobre el equipo..."
          rows={3}
        />
      </div>
    </div>
  );

  // New appraisal form
  if (!id || id === 'new') {
    return (
      <div className="space-y-6" data-testid="new-appraisal-page">
        <Button variant="ghost" onClick={() => navigate('/apps/appraisal')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Tasaciones
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Nueva Tasación de Equipo
            </CardTitle>
            <CardDescription>Ingresa las especificaciones del equipo a tasar</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderFormFields()}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => navigate('/apps/appraisal')}>Cancelar</Button>
              <Button onClick={handleCreate} disabled={saving}>
                {saving ? 'Guardando...' : 'Crear Tasación'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Edit mode view
  if (editMode) {
    return (
      <div className="space-y-6" data-testid="edit-appraisal-page">
        <Button variant="ghost" onClick={() => setEditMode(false)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Cancelar Edición
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              Editar Tasación
            </CardTitle>
            <CardDescription>Modifica las especificaciones y vuelve a buscar comparables</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {renderFormFields()}
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancelar</Button>
              <Button onClick={handleUpdate} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar y Volver a Tasar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Existing appraisal view
  return (
    <div className="space-y-6" data-testid="appraisal-detail-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/apps/appraisal')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a Tasaciones
        </Button>
        
        {/* Edit button */}
        <Button variant="outline" onClick={handleResetAndEdit}>
          <Edit className="w-4 h-4 mr-2" />
          Editar y Re-tasar
        </Button>
      </div>

      {/* Asset Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-600" />
                {appraisal?.asset?.year} {appraisal?.asset?.make} {appraisal?.asset?.model}
              </CardTitle>
              <CardDescription>
                {appraisal?.client_name ? `Cliente: ${appraisal.client_name}` : 'Sin cliente asociado'}
              </CardDescription>
            </div>
            <Badge variant={appraisal?.status === 'completed' ? 'default' : 'secondary'}>
              {appraisal?.status === 'completed' ? 'Completada' : 'Borrador'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground">Millaje</p>
              <p className="font-semibold">{appraisal?.asset?.mileage?.toLocaleString() || 'N/A'} mi</p>
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground">
                {['truck'].includes(appraisal?.asset?.asset_type) ? 'Sleeper' : 
                 appraisal?.asset?.asset_type === 'pickup' ? 'Cabina' : 
                 appraisal?.asset?.asset_type === 'trailer' ? 'Tipo' : 'Detalle'}
              </p>
              <p className="font-semibold">
                {appraisal?.asset?.sleeper_type || appraisal?.asset?.cab_type || 
                 TRAILER_TYPES.find(t => t.value === appraisal?.asset?.trailer_type)?.label ||
                 appraisal?.asset?.body_length || 'N/A'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground">Condición</p>
              <p className="font-semibold">{appraisal?.asset?.condition || 'N/A'}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground">Precio Solicitado</p>
              <p className="font-semibold">${appraisal?.asset?.requested_price?.toLocaleString() || 'N/A'}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <p className="text-xs text-muted-foreground">Valor Tasado</p>
              <p className="font-semibold text-blue-600">
                ${appraisal?.appraised_value?.toLocaleString() || 'Pendiente'}
              </p>
            </div>
          </div>
          
          {/* Additional specs if available */}
          {(appraisal?.asset?.engine_make || appraisal?.asset?.transmission || appraisal?.asset?.axle_config) && (
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
              {appraisal?.asset?.engine_make && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Motor: </span>
                  <span className="font-medium">{appraisal.asset.engine_make}</span>
                </div>
              )}
              {appraisal?.asset?.transmission && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Transmisión: </span>
                  <span className="font-medium">{appraisal.asset.transmission}</span>
                </div>
              )}
              {appraisal?.asset?.axle_config && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Ejes: </span>
                  <span className="font-medium">{appraisal.asset.axle_config}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Market Comps */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Comparables del Mercado</CardTitle>
              <CardDescription>
                Equipos similares: {appraisal?.asset?.year} {appraisal?.asset?.make} {appraisal?.asset?.model}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSearchComps} disabled={searchingComps}>
                {searchingComps ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Buscar Retail
              </Button>
              <Button onClick={handleSearchAuction} disabled={searchingAuction} variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50">
                {searchingAuction ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <TrendingDown className="w-4 h-4 mr-2" />}
                Buscar Subasta
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {appraisal?.market_comps?.length > 0 ? (
            <div className="space-y-3">
              {/* Data Source Notice - Dynamic based on scraping result */}
              {appraisal?.scraping_source === 'real' ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">Datos Reales del Mercado</p>
                    <p>Precios obtenidos de listados reales para {appraisal?.asset?.year} {appraisal?.asset?.make} {appraisal?.asset?.model}. Haz clic en "Verificar" para ver el listado original.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium">Datos de Mercado Simulados</p>
                    <p>Los precios son estimaciones basadas en tendencias del mercado para {appraisal?.asset?.year} {appraisal?.asset?.make} {appraisal?.asset?.model}. El scraping web no pudo obtener datos reales. Haz clic en "Verificar" para buscar listados manualmente.</p>
                  </div>
                </div>
              )}

              {appraisal.market_comps.map((comp, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <p className="font-medium">{comp.year} {comp.make} {comp.model}</p>
                    <p className="text-sm text-muted-foreground">
                      {comp.mileage?.toLocaleString()} mi · {comp.sleeper_type || comp.cab_type || comp.trailer_type || 'N/A'} · {comp.condition || 'N/A'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Fuente: {comp.source}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-lg">${comp.price?.toLocaleString()}</p>
                      {comp.auction_price && (
                        <p className="text-xs text-amber-600">Subasta: ${comp.auction_price?.toLocaleString()}</p>
                      )}
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href={comp.url} target="_blank" rel="noopener noreferrer" title="Verificar en sitio de origen">
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Verificar
                      </a>
                    </Button>
                  </div>
                </div>
              ))}

              {/* Auction Comparables Section */}
              {appraisal?.auction_comps?.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Gavel className="w-5 h-5 text-amber-600" />
                      <h4 className="font-semibold text-amber-700">Datos de Subasta</h4>
                      <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                        {appraisal.auction_source === 'live' ? 'EN VIVO' : 'ESTIMADO'}
                      </Badge>
                    </div>
                    
                    {appraisal.auction_source === 'live' ? (
                      <div className="flex items-center gap-2 p-2 rounded bg-emerald-50 text-emerald-700 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        <span>Precios obtenidos de subastas reales (Ritchie Bros, Purple Wave, IronPlanet)</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-2 rounded bg-amber-50 text-amber-700 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Valores estimados basados en datos de mercado J.D. Power y NADA</span>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {appraisal.auction_comps.map((comp, idx) => (
                        <div key={idx} className="p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-amber-800">{comp.source}</span>
                            <span className="text-lg font-bold text-amber-700">${comp.auction_price?.toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-amber-600 mt-1">{comp.condition || 'Vendido en subasta'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <Separator className="my-4" />

              {/* Calculated Values */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                  <p className="text-sm text-muted-foreground">Precio Promedio</p>
                  <p className="text-xl font-semibold text-emerald-700 dark:text-emerald-400">
                    ${appraisal.average_price?.toLocaleString() || 'N/A'}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                  <p className="text-sm text-muted-foreground">Promedio Subasta</p>
                  <p className="text-xl font-semibold text-amber-700 dark:text-amber-400">
                    ${appraisal.average_auction_price?.toLocaleString() || (appraisal.average_price ? Math.round(appraisal.average_price * 0.8).toLocaleString() : 'N/A')}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <p className="text-sm text-muted-foreground">Precio Más Bajo</p>
                  <p className="text-xl font-semibold text-blue-700 dark:text-blue-400">
                    ${appraisal.lowest_price?.toLocaleString() || 'N/A'}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-primary/10">
                  <p className="text-sm text-muted-foreground">Valor Tasado</p>
                  <p className="text-xl font-semibold text-primary">
                    ${appraisal.appraised_value?.toLocaleString() || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Variance Warning */}
              {appraisal.has_variance_warning && (
                <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">ALERTA: Varianza Alta ({appraisal.price_diff_pct?.toFixed(1)}%)</p>
                    <p className="text-sm">El valor tasado difiere significativamente del precio solicitado. Revise los comparables.</p>
                  </div>
                </div>
              )}

              {!appraisal.appraised_value && (
                <div className="flex justify-end">
                  <Button onClick={handleCalculate} disabled={calculating}>
                    {calculating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                    Calcular Valor Tasado
                  </Button>
                </div>
              )}
              
              {appraisal.appraised_value && (
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={handleResetAndEdit}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Modificar y Re-tasar
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No hay comparables aún</p>
              <p className="text-sm">Haz clic en "Buscar Comparables" para obtener precios del mercado</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetAppraisalApp;
